/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';
import { PortableText } from '@portabletext/react';
import {
  Box,
  Text,
  UnorderedList,
  OrderedList,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import styled from 'styled-components';
import SanityImage from 'gatsby-plugin-sanity-image';

import Link from '@repo/ui-components/tailwind/Link';
import { getInternalLink } from '@repo/utils';
import {
  BlockBuilderImage,
  BlockBuilderLinkGrid,
  BlockBuilderTestimonial,
  MediaGrid,
  VideoGallery,
} from '../../../sanity/types';

const BoxWithScrollbars = styled(Box)`
  .show-scroll-bars {
    ::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 1px;
      height: 3px;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 8px;
      background-color: rgba(0, 0, 0, 0.5);
      box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
    }
  }
`;

function RichText({ body }) {
  const components = {
    types: {
      blockBuilderTestimonial: BlockBuilderTestimonial,
      // Redundant types. Keeping them both for backwards compatibility
      blockContentImage: BlockBuilderImage,
      blockImage: BlockBuilderImage,
      blockContentLinkGrid: BlockBuilderLinkGrid,
      mediaGrid: ({ value }) => (
        <MediaGrid value={value} getInternalLink={getInternalLink} />
      ),
      videoGallery: VideoGallery,
      blockBuilderImage: ({ value }) => (
        <SanityImage className="blockBuilderImage" my="2" {...value} />
      ),
      deptBuilderImage: ({ value }) => (
        <SanityImage
          className="deptBuilderImage"
          my="2"
          {...value}
          style={{
            height: value.asset.height,
            width: value.asset.width,
          }}
        />
      ),
      image: ({ value }) => (
        <SanityImage
          className="image"
          my="2"
          {...value}
          style={{
            height: value.asset.height,
            width: value.asset.width,
          }}
        />
      ),
      inlineIcon: ({ value }) => (
        <SanityImage
          alt="Inline Icon"
          {...value}
          style={{
            height: value?.iconHeight || '16',
            width: value?.iconWidth || '16',
            marginRight: '.25rem',
          }}
        />
      ),
      table: ({ value }) => (
        <BoxWithScrollbars key={value._key}>
          <Box w="full" overflowX="scroll" className="show-scroll-bars">
            <Table variant="simple">
              <Thead>
                <Tr>
                  {value.rows[0].cells.map((cell, cellIndex) => (
                    <Th
                      // We need the index here because all we get are raw strings and sometimes they repeat
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${cell}-${cellIndex}`}
                      whiteSpace="nowrap"
                      p={2.5}
                      fontWeight="extrabold"
                      textTransform="none"
                      borderRight={[
                        cellIndex < value.rows[0].cells.length - 1 &&
                          '1px solid',
                        null,
                        '0px',
                      ]}
                      borderColor="stroke.light"
                      fontSize="sm"
                      letterSpacing="normal"
                      color="typography.base"
                      width={`${100 / value.rows[0].cells.length}%`}
                    >
                      {cell}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {value.rows.slice(1).map(row => (
                  <Tr key={row._key}>
                    {row.cells.map((cell, cellIndex) => (
                      <Td
                        key={uniqueId(cell._key)}
                        p={2.5}
                        borderRight={[
                          cellIndex < row.cells.length - 1 && '1px solid',
                          null,
                          '0px',
                        ]}
                        borderColor="stroke.light"
                        width={`${100 / value.rows[0].cells.length}%`}
                      >
                        <Text>{cell}</Text>
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </BoxWithScrollbars>
      ),
    },
    marks: {
      link: ({ children, value }) => (
        <Link className="underline" link={value}>
          {children}
        </Link>
      ),
      strong: ({ children }) => <b>{children}</b>,
      em: ({ children }) => <em>{children}</em>,
    },
    block: {
      normal: ({ children }) => (
        <Text textStyle="body" as="p" my={4}>
          {children}
        </Text>
      ),
      blockquote: ({ children }) => <blockquote>{children}</blockquote>,
      h1: ({ children }) => (
        <Text textStyle="h1" as="h1" my={4}>
          {children}
        </Text>
      ),
      h2: ({ children }) => (
        <Text textStyle="h2" as="h2" my={4}>
          {children}
        </Text>
      ),
      h3: ({ children }) => (
        <Text textStyle="h3" as="h3" my={4}>
          {children}
        </Text>
      ),
      h4: ({ children }) => (
        <Text textStyle="h4_5_6" as="h4" my={4}>
          {children}
        </Text>
      ),
      h5: ({ children }) => (
        <Text textStyle="h4_5_6" as="h5" my={4}>
          {children}
        </Text>
      ),
      h6: ({ children }) => (
        <Text textStyle="h4_5_6" as="h6" my={4}>
          {children}
        </Text>
      ),
    },
    list: {
      bullet: ({ children }) => (
        <UnorderedList sx={{ li: { mb: 1 } }}>{children}</UnorderedList>
      ),
      number: ({ children }) => (
        <OrderedList sx={{ li: { mb: 1 } }}>{children}</OrderedList>
      ),
    },
  };

  return <PortableText value={body} components={components} />;
}

RichText.propTypes = {
  body: PropTypes.arrayOf(
    PropTypes.shape({
      children: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
        })
      ),
    })
  ),
};

RichText.defaultProps = {
  body: null,
};

export default RichText;
